"use client"

import {
  AreaChart as RAreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Brush, ReferenceLine, ReferenceArea,
} from "recharts"
import {
  type BaseChartProps, DEFAULT_PALETTE, inferSeries, colorFor, type ChartDatum,
} from "./types"
import { useMemo } from "react"

export interface AreaChartProps extends BaseChartProps {
  /** Stack areas instead of overlaying. */
  stacked?: boolean
  /** Stack and normalise to 100% (each row sums to 100). Implies stacked. */
  percent?: boolean
  /** Render straight (linear) instead of monotone-curved areas. */
  straight?: boolean
  /** Render as a stepped area. */
  stepped?: boolean
  /** Render with a vertical fill gradient. */
  gradient?: boolean
  /** Render the chart vertically (Y-axis is the category). */
  vertical?: boolean
  /** Add a brush (range selector) below the chart. */
  withBrush?: boolean
  /** Sync this chart's tooltip with another chart that shares the same id. */
  syncId?: string
  /** Skip nulls and connect across the gap. */
  connectNulls?: boolean
  /** Reference horizontal lines (e.g. target threshold). */
  referenceLines?: { y: number; label?: string; color?: string }[]
  /** Reference vertical band(s). */
  referenceAreas?: { x1: string | number; x2: string | number; label?: string; color?: string }[]
  /** Map a per-row color from the value of `fillByKey`. Typically used to
   *  visualise a categorical band (e.g. positive/negative shading). */
  fillByKey?: string
  /** When `fillByKey` is set, this maps the value to a color. */
  fillByMap?: (v: number | string | undefined) => string
}

export function AreaChart({
  data, series, xKey = "name", palette = DEFAULT_PALETTE, colorMap,
  showLegend = true, showGrid = true, showTooltip = true,
  xLabel, yLabel, compact, className, style, height = "100%", width = "100%",
  stacked, percent, straight, stepped, gradient, vertical, withBrush, syncId, connectNulls,
  referenceLines, referenceAreas, fillByKey, fillByMap,
}: AreaChartProps) {
  const seriesList = series ?? inferSeries(data, xKey)
  const lineType: "linear" | "monotone" | "step" = stepped ? "step" : straight ? "linear" : "monotone"
  const isStacked = !!(stacked || percent)

  const normalised = useMemo<ChartDatum[]>(() => {
    if (!percent) return data
    return data.map(row => {
      const sum = seriesList.reduce((s, k) => s + (Number(row[k]) || 0), 0)
      const out: ChartDatum = { ...row }
      seriesList.forEach(k => { out[k] = sum > 0 ? (Number(row[k]) || 0) * 100 / sum : 0 })
      return out
    })
  }, [data, seriesList, percent])

  return (
    <ResponsiveContainer width={width} height={height} className={className} style={style}>
      <RAreaChart data={normalised} layout={vertical ? "vertical" : "horizontal"} syncId={syncId} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
        <defs>
          {gradient && seriesList.map((name, i) => {
            const c = colorFor(name, i, palette, colorMap)
            return (
              <linearGradient key={name} id={`area-grad-${name.replace(/\W/g, "_")}-${i}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={c} stopOpacity={0.8} />
                <stop offset="95%" stopColor={c} stopOpacity={0.05} />
              </linearGradient>
            )
          })}
          {fillByKey && fillByMap && (
            <linearGradient id="area-fill-by-value" x1="0" y1="0" x2="1" y2="0">
              {data.map((row, i) => {
                const c = fillByMap(row[fillByKey] as number | string | undefined)
                const offset = data.length === 1 ? "100%" : `${(i / (data.length - 1)) * 100}%`
                return <stop key={i} offset={offset} stopColor={c} />
              })}
            </linearGradient>
          )}
        </defs>
        {showGrid && !compact && <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.18} />}
        {!compact && (vertical
          ? <XAxis type="number" tick={{ fontSize: 10.5 }} label={xLabel ? { value: xLabel, position: "insideBottom", offset: -4 } : undefined} />
          : <XAxis dataKey={xKey} tick={{ fontSize: 10.5 }} label={xLabel ? { value: xLabel, position: "insideBottom", offset: -4 } : undefined} interval={0} angle={-15} dy={6} height={50} />)}
        {!compact && (vertical
          ? <YAxis dataKey={xKey} type="category" tick={{ fontSize: 10.5 }} width={80} />
          : <YAxis tick={{ fontSize: 10.5 }} label={yLabel ? { value: yLabel, angle: -90, position: "insideLeft" } : undefined} />)}
        {showTooltip && <Tooltip cursor={{ fill: "hsl(var(--foreground) / 0.06)", stroke: "hsl(var(--muted-foreground))", strokeOpacity: 0.35 }} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 11, color: "hsl(var(--card-foreground))" }} />}
        {showLegend && !compact && <Legend wrapperStyle={{ fontSize: 11 }} />}
        {referenceAreas?.map((r, i) => (
          <ReferenceArea key={`ra-${i}`} x1={r.x1} x2={r.x2} stroke={r.color || "#888"} fill={r.color || "#888"} fillOpacity={0.08} label={r.label ? { value: r.label, fontSize: 10 } : undefined} />
        ))}
        {referenceLines?.map((r, i) => (
          <ReferenceLine key={`rl-${i}`} y={r.y} stroke={r.color || "#888"} strokeDasharray="4 4" label={r.label ? { value: r.label, fontSize: 10 } : undefined} />
        ))}
        {seriesList.map((name, i) => {
          const c = colorFor(name, i, palette, colorMap)
          const fill = fillByKey ? "url(#area-fill-by-value)" : (gradient ? `url(#area-grad-${name.replace(/\W/g, "_")}-${i})` : c)
          return (
            <Area
              key={name}
              type={lineType}
              dataKey={name}
              stroke={c}
              fill={fill}
              fillOpacity={gradient || fillByKey ? 1 : 0.35}
              stackId={isStacked ? "a" : undefined}
              connectNulls={connectNulls}
              isAnimationActive={false}
            />
          )
        })}
        {withBrush && <Brush dataKey={xKey} height={20} stroke={palette[0]} />}
      </RAreaChart>
    </ResponsiveContainer>
  )
}
